import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import { Link } from "react-router-dom";
import { Trash2, FolderInput, Sparkles, X, Tag, Check, BellRing, Bookmark, Mail } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Everyday", "Workwear", "Occasion", "Athletic", "Gifts", "For kids"];

const expiryLabel = (iso) => {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "expired";
  const h = Math.ceil(ms / 3.6e6);
  return h > 24 ? `${Math.ceil(h / 24)}d left` : `${h}h left`;
};

export default function Saved() {
  const [likes, setLikes] = useState([]);
  const [matches, setMatches] = useState([]);
  const [offers, setOffers] = useState([]);
  const [brandAlerts, setBrandAlerts] = useState([]);
  const [watching, setWatching] = useState([]);
  const [tab, setTab] = useState("saved");
  const [moveFor, setMoveFor] = useState(null); // like id
  const [counterFor, setCounterFor] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [savedSearches, setSavedSearches] = useState([]);

  const load = async () => {
    const [l, m, o, b, f, s] = await Promise.all([
      api.get("/likes"),
      api.get("/notifications/matches"),
      api.get("/offers"),
      api.get("/notifications/brand-alerts"),
      api.get("/brands/following"),
      api.get("/saved-searches").catch(() => ({ data: [] })),
    ]);
    setLikes(l.data);
    setMatches(m.data.matches || []);
    setOffers(o.data || []);
    setBrandAlerts(b.data.alerts || []);
    setWatching(f.data || []);
    setSavedSearches(s.data || []);
  };
  useEffect(() => { load(); }, []);

  const del = async (like_id) => {
    await api.delete(`/likes/${like_id}`);
    toast.success("Removed");
    load();
  };
  const searchUrl = (params) => "/browse?" + new URLSearchParams(params || {}).toString();
  const deleteSearch = async (id) => {
    await api.delete(`/saved-searches/${id}`);
    toast.success("Saved search removed");
    load();
  };
  const seenSearch = async (id) => { await api.post(`/notifications/saved-searches/${id}/seen`); load(); };
  const toggleEmail = async (id, current) => {
    setSavedSearches((s) => s.map((x) => (x.id === id ? { ...x, email_alerts: !current } : x)));
    try {
      await api.patch(`/saved-searches/${id}`, { email_alerts: !current });
      toast.success(!current ? "Email alerts on — we'll email you new matches" : "Email alerts off");
    } catch (e) {
      setSavedSearches((s) => s.map((x) => (x.id === id ? { ...x, email_alerts: current } : x)));
      toast.error("Could not update email alerts");
    }
  };
  const move = async (like_id, category) => {
    await api.patch(`/likes/${like_id}/category`, null, { params: { category } });
    setMoveFor(null); toast.success("Moved to " + category);
    load();
  };
  const dismiss = async (lid) => { await api.post(`/notifications/dismiss/${lid}`); load(); };
  const unwatch = async (brand) => {
    await api.delete(`/brands/follow/${encodeURIComponent(brand)}`);
    toast.success(`Stopped watching ${brand}`);
    load();
  };
  const acceptOffer = async (id) => {
    await api.post(`/offers/${id}/accept`);
    toast.success("Offer accepted — added to your bag at the offer price");
    load();
  };
  const declineOffer = async (id) => {
    await api.post(`/offers/${id}/decline`);
    toast.success("Offer declined");
    load();
  };
  const respondOffer = async (id, action, counter_price) => {
    try {
      await api.post(`/offers/${id}/respond`, { action, counter_price });
      toast.success(action === "accept" ? "Offer accepted — ready to buy" : action === "counter" ? "Counter sent to the seller" : "Offer declined");
      setCounterFor(null); setCounterPrice(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not respond"); }
  };
  const buyOffer = async (id) => {
    try {
      await api.post(`/offers/${id}/buy`);
      toast.success("Added to your bag at the agreed price");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to bag"); }
  };

  const soldInfo = (l) => {
    if (!l.sold) return 0;
    const ms = l.sold_expires_at ? (new Date(l.sold_expires_at) - new Date()) : 0;
    return ms > 0 ? Math.ceil(ms / 86400000) : 0;
  };
  const LikeRow = (l) => (
    <li key={l.id} data-testid={`saved-row-${l.id}`} className="py-4 flex items-center gap-4">
      <Link to={`/product/${l.listing.id}`} className="shrink-0">
        <div className="w-16 h-16 hairline overflow-hidden">
          <ImageThumb path={l.listing.image_paths?.[0]} className={`w-full h-full object-cover ${l.sold ? "opacity-60" : ""}`}/>
        </div>
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link to={`/product/${l.listing.id}`} className="font-display text-base truncate">{l.listing.title}</Link>
          {l.sold && <span className="text-[0.58rem] uppercase tracking-wider px-1.5 py-0.5 shrink-0" style={{background:"#E11D48",color:"#fff"}} data-testid={`sold-badge-${l.id}`}>Sold</span>}
        </div>
        {l.sold ? (
          <div className="text-[0.72rem] text-neutral-500 mt-1" data-testid={`sold-note-${l.id}`}>
            This item sold — it'll leave your list in {soldInfo(l)} day{soldInfo(l) === 1 ? "" : "s"} unless you remove it.
          </div>
        ) : (
          <div className="text-xs text-neutral-500 mt-1">
            {l.liked_color && <span className="mr-3">color: <span className="text-black">{l.liked_color}</span></span>}
            {l.liked_pattern && <span className="mr-3">pattern: <span className="text-black">{l.liked_pattern}</span></span>}
            {l.liked_style && <span>style: <span className="text-black">{l.liked_style}</span></span>}
          </div>
        )}
      </div>
      <div className="font-display text-sm">${Number(l.listing.price).toFixed(2)}</div>
      {!l.for_later && !l.sold && (
        <button onClick={()=>setMoveFor(l.id)} data-testid={`move-${l.id}`} className="flex flex-col items-center p-2 hover:bg-black/5" title="Move to category">
          <FolderInput size={16}/>
          <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Move</span>
        </button>
      )}
      <button onClick={()=>del(l.id)} data-testid={`delete-${l.id}`} className="flex flex-col items-center p-2 hover:bg-black/5" title="Delete">
        <Trash2 size={16}/>
        <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Remove</span>
      </button>
    </li>
  );
  const forLater = likes.filter(l => l.for_later);
  const grouped = likes.filter(l => !l.for_later).reduce((acc, l) => {
    const k = l.category || "Uncategorized";
    (acc[k] = acc[k] || []).push(l);
    return acc;
  }, {});

  return (
    <div data-testid="saved-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Your Wardrobe Radar</div>
          <h1 className="font-display text-5xl tracking-tighter mt-2">Saved.</h1>
        </div>
        <div className="flex gap-2">
          <button className="chip" data-active={tab==="saved"} onClick={()=>setTab("saved")} data-testid="tab-saved">Saved items</button>
          <button className="chip" data-active={tab==="later"} onClick={()=>setTab("later")} data-testid="tab-later">
            Save for later {forLater.length > 0 && (
              <span className="ml-2 bg-black/10 text-black px-1.5 py-0.5 rounded-full text-[0.65rem]">{forLater.length}</span>
            )}
          </button>
          <button
            className={`chip relative ${offers.length ? "klein-glow" : ""}`}
            data-active={tab==="offers"}
            onClick={()=>setTab("offers")}
            data-testid="tab-offers"
            style={offers.length ? {background:"#2563EB", color:"#fff", borderColor:"#2563EB"} : {}}
          >
            Seller offers {offers.length > 0 && (
              <span className="ml-2 bg-white/20 text-white px-1.5 py-0.5 rounded-full text-[0.65rem]">{offers.length}</span>
            )}
          </button>
          <button
            className={`chip relative ${brandAlerts.length ? "klein-glow" : ""}`}
            data-active={tab==="brands"}
            onClick={()=>setTab("brands")}
            data-testid="tab-brand-alerts"
            style={brandAlerts.length ? {background:"#2563EB", color:"#fff", borderColor:"#2563EB"} : {}}
          >
            Brand alerts {brandAlerts.length > 0 && (
              <span className="ml-2 bg-white/20 text-white px-1.5 py-0.5 rounded-full text-[0.65rem]">{brandAlerts.length}</span>
            )}
          </button>
          <button
            className={`chip relative ${matches.length ? "klein-glow" : ""}`}
            data-active={tab==="matches"}
            onClick={()=>setTab("matches")}
            data-testid="tab-matches"
            style={matches.length ? {background:"#2563EB", color:"#fff", borderColor:"#2563EB"} : {}}
          >
            New matches {matches.length > 0 && (
              <span className="ml-2 bg-white/20 text-white px-1.5 py-0.5 rounded-full text-[0.65rem]">{matches.length}</span>
            )}
          </button>
          {(() => {
            const newTotal = savedSearches.reduce((a, s) => a + (s.new_count || 0), 0);
            return (
              <button
                className={`chip relative ${newTotal ? "klein-glow" : ""}`}
                data-active={tab === "searches"}
                onClick={() => setTab("searches")}
                data-testid="tab-searches"
                style={newTotal ? { background: "#2563EB", color: "#fff", borderColor: "#2563EB" } : {}}
              >
                Saved searches {newTotal > 0 && (
                  <span className="ml-2 bg-white/20 text-white px-1.5 py-0.5 rounded-full text-[0.65rem]">{newTotal}</span>
                )}
              </button>
            );
          })()}
        </div>
      </div>

      {tab === "saved" ? (
        Object.keys(grouped).length === 0 ? (
          <div className="hairline p-16 text-center mt-10">
            <div className="label-eyebrow">Nothing saved yet</div>
            <p className="mt-3 text-neutral-500">Tap the heart on any item to save it — and lock in the criteria you love.</p>
            <Link to="/browse" className="btn-primary mt-6 inline-flex">Start browsing</Link>
          </div>
        ) : (
          <div className="mt-10 space-y-14">
            {Object.entries(grouped).map(([cat, items]) => (
              <section key={cat}>
                <div className="flex items-baseline justify-between hairline-b pb-3">
                  <h2 className="font-display text-2xl">{cat}</h2>
                  <span className="label-eyebrow">{items.length} item{items.length>1?"s":""}</span>
                </div>
                <ul className="mt-6 divide-y divide-black/10">
                  {items.map(l => LikeRow(l))}
                </ul>
              </section>
            ))}
          </div>
        )
      ) : tab === "later" ? (
        forLater.length === 0 ? (
          <div className="hairline p-16 text-center mt-10" data-testid="later-empty">
            <div className="label-eyebrow">Nothing saved for later</div>
            <p className="mt-3 text-neutral-500">Use “Save for later” on the homepage nudge to stash items you're still deciding on.</p>
            <Link to="/browse" className="btn-primary mt-6 inline-flex">Start browsing</Link>
          </div>
        ) : (
          <div className="mt-10" data-testid="later-list">
            <div className="flex items-baseline justify-between hairline-b pb-3">
              <h2 className="font-display text-2xl">Save for later</h2>
              <span className="label-eyebrow">{forLater.length} item{forLater.length>1?"s":""}</span>
            </div>
            <ul className="mt-6 divide-y divide-black/10">
              {forLater.map(l => LikeRow(l))}
            </ul>
          </div>
        )
      ) : tab === "offers" ? (
        <div className="mt-10">
          <div className="hairline p-6 mb-6" style={{borderColor:"#2563EB"}}>
            <div className="flex items-center gap-2">
              <Tag size={16} style={{color:"#2563EB"}}/>
              <span className="label-eyebrow" style={{color:"#2563EB"}}>Your offers</span>
            </div>
            <p className="mt-2 text-sm text-neutral-600">Offers you've made and private offers from sellers. A seller can accept, decline, or counter — you have 48 hours to answer a counter.</p>
          </div>
          {offers.length === 0 ? (
            <div className="hairline p-16 text-center">
              <div className="label-eyebrow">No offers right now</div>
              <p className="mt-3 text-neutral-500">Make an offer from any product page, or wait for a seller to send you a private discount.</p>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {offers.map(o => {
                const legacy = o.initiator === "seller";
                const waitingSeller = o.status === "active" && o.turn === "seller";
                const yourTurn = o.status === "active" && o.turn === "buyer";
                const accepted = o.status === "accepted";
                return (
                  <li key={o.id} className="py-4 flex flex-wrap items-center gap-4" data-testid={`offer-row-${o.id}`}>
                    <Link to={`/product/${o.listing.id}`} className="shrink-0">
                      <div className="w-16 h-16 hairline overflow-hidden">
                        <ImageThumb path={o.listing.image_paths?.[0]} className="w-full h-full object-cover"/>
                      </div>
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link to={`/product/${o.listing.id}`} className="font-display text-base truncate block">{o.listing.title}</Link>
                      <div className="text-xs text-neutral-500 mt-1">
                        {legacy ? `from ${o.seller_name || "seller"}` : (accepted ? "Accepted — ready to buy" : waitingSeller ? "Waiting for the seller" : "Seller countered — your move")}
                        {o.expires_at && !accepted && <span data-testid={`offer-expiry-${o.id}`}> · {expiryLabel(o.expires_at)}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-neutral-400 line-through">${Number(o.original_price).toFixed(2)}</div>
                      <div className="font-display text-base" style={{color:"#2563EB"}} data-testid={`offer-price-${o.id}`}>${Number(o.offer_price).toFixed(2)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {legacy && (<>
                        <button onClick={()=>acceptOffer(o.id)} className="btn-klein text-[0.68rem]" data-testid={`accept-offer-${o.id}`}><Check size={13}/> Accept</button>
                        <button onClick={()=>declineOffer(o.id)} className="flex flex-col items-center p-2 hover:bg-black/5" title="Decline" data-testid={`decline-offer-${o.id}`}><X size={16}/><span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Decline</span></button>
                      </>)}
                      {accepted && (
                        <button onClick={()=>buyOffer(o.id)} className="btn-klein text-[0.68rem]" data-testid={`buy-offer-${o.id}`}><Check size={13}/> Buy ${Number(o.offer_price).toFixed(2)}</button>
                      )}
                      {waitingSeller && <span className="text-xs text-neutral-400" data-testid={`offer-waiting-${o.id}`}>Pending seller</span>}
                      {yourTurn && (<>
                        <button onClick={()=>respondOffer(o.id, "accept")} className="btn-klein text-[0.68rem]" data-testid={`accept-counter-${o.id}`}><Check size={13}/> Accept</button>
                        <button onClick={()=>setCounterFor(counterFor===o.id?null:o.id)} className="btn-outline text-[0.68rem]" data-testid={`counter-offer-${o.id}`}>Counter</button>
                        <button onClick={()=>respondOffer(o.id, "decline")} className="flex flex-col items-center p-2 hover:bg-black/5" title="Decline" data-testid={`decline-counter-${o.id}`}><X size={16}/><span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Decline</span></button>
                      </>)}
                    </div>
                    {yourTurn && counterFor===o.id && (
                      <div className="w-full flex items-center gap-2 sm:pl-20" data-testid={`counter-box-${o.id}`}>
                        <input type="number" value={counterPrice} onChange={(e)=>setCounterPrice(e.target.value)} placeholder="Your counter price" className="hairline p-2 text-sm w-40" data-testid={`counter-input-${o.id}`}/>
                        <button onClick={()=>respondOffer(o.id, "counter", parseFloat(counterPrice))} className="btn-primary text-[0.68rem]" data-testid={`counter-send-${o.id}`}>Send counter</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : tab === "brands" ? (
        <div className="mt-10">
          <div className="hairline p-6 mb-6" style={{borderColor:"#2563EB"}}>
            <div className="flex items-center gap-2">
              <BellRing size={16} style={{color:"#2563EB"}}/>
              <span className="label-eyebrow" style={{color:"#2563EB"}}>Your brand wanted list</span>
            </div>
            <p className="mt-2 text-sm text-neutral-600">
              New arrivals from the heritage labels you watch. This tab lights up the moment one lands.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {watching.length === 0 ? (
                <Link to="/brands" className="btn-klein text-[0.68rem]" data-testid="watch-brands-cta">Pick brands to watch</Link>
              ) : (
                <>
                  {watching.map(w => (
                    <button key={w.brand_key} className="chip" data-active="true" onClick={()=>unwatch(w.brand)}
                      data-testid={`saved-unwatch-${w.brand}`}>{w.brand} ×</button>
                  ))}
                  <Link to="/brands" className="chip" data-testid="add-more-brands">+ Add more</Link>
                </>
              )}
            </div>
          </div>
          {brandAlerts.length === 0 ? (
            <div className="hairline p-16 text-center">
              <div className="label-eyebrow">Nothing new from your brands</div>
              <p className="mt-3 text-neutral-500">
                {watching.length ? "We'll light this tab up in blue the moment one of them lands." : "Watch a brand and we'll alert you here."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {brandAlerts.map(a => (
                <li key={a.listing.id} className="py-4 flex items-center gap-4" data-testid={`brand-alert-row-${a.listing.id}`}>
                  <Link to={`/product/${a.listing.id}`} className="shrink-0">
                    <div className="w-16 h-16 hairline overflow-hidden">
                      <ImageThumb path={a.listing.image_paths?.[0]} className="w-full h-full object-cover"/>
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="label-eyebrow" style={{color:"#2563EB"}}>{a.brand}</div>
                    <Link to={`/product/${a.listing.id}`} className="font-display text-base truncate block">{a.listing.title}</Link>
                    <div className="text-xs text-neutral-500 mt-1">{a.listing.color} · {a.listing.pattern} · {a.listing.condition === "new" ? "New" : "Pre-loved"}</div>
                  </div>
                  <div className="font-display text-sm">${Number(a.listing.price).toFixed(2)}</div>
                  <button onClick={()=>dismiss(a.listing.id)} className="flex flex-col items-center p-2 hover:bg-black/5" title="Dismiss"
                    data-testid={`dismiss-brand-${a.listing.id}`}>
                    <X size={16}/>
                    <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Dismiss</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : tab === "matches" ? (
        <div className="mt-10">
          <div className="hairline p-6 mb-6" style={{borderColor:"#2563EB"}}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} style={{color:"#2563EB"}}/>
              <span className="label-eyebrow" style={{color:"#2563EB"}}>Matches by pattern, color &amp; style</span>
            </div>
            <p className="mt-2 text-sm text-neutral-600">These listings match what you saved. Dismiss the ones you don't want, or save them into a category.</p>
          </div>
          {matches.length === 0 ? (
            <div className="hairline p-16 text-center">
              <div className="label-eyebrow">You're all caught up</div>
              <p className="mt-3 text-neutral-500">We'll light this tab up in blue when new matching listings arrive.</p>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {matches.map(m => (
                <li key={m.id} className="py-4 flex items-center gap-4" data-testid={`match-row-${m.id}`}>
                  <Link to={`/product/${m.id}`} className="shrink-0">
                    <div className="w-16 h-16 hairline overflow-hidden">
                      <ImageThumb path={m.image_paths?.[0]} className="w-full h-full object-cover"/>
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/product/${m.id}`} className="font-display text-base truncate block">{m.title}</Link>
                    <div className="text-xs text-neutral-500 mt-1">{m.color} · {m.pattern} · {m.style}</div>
                  </div>
                  <div className="font-display text-sm">${Number(m.price).toFixed(2)}</div>
                  <button onClick={()=>dismiss(m.id)} className="flex flex-col items-center p-2 hover:bg-black/5" title="Dismiss" data-testid={`dismiss-${m.id}`}>
                    <X size={16}/>
                    <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Dismiss</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-10" data-testid="saved-searches-tab">
          <div className="hairline p-6 mb-6" style={{borderColor:"#2563EB"}}>
            <div className="flex items-center gap-2">
              <Bookmark size={16} style={{color:"#2563EB"}}/>
              <span className="label-eyebrow" style={{color:"#2563EB"}}>Your saved searches</span>
            </div>
            <p className="mt-2 text-sm text-neutral-600">Save a search from the Browse page (e.g. “leather · size M · $20–200”). We'll flag new matches here and in the bell.</p>
          </div>
          {savedSearches.length === 0 ? (
            <div className="hairline p-16 text-center" data-testid="saved-searches-empty">
              <div className="label-eyebrow">No saved searches yet</div>
              <p className="mt-3 text-neutral-500">Filter the Browse page how you like, then tap “Save this search”.</p>
              <Link to="/browse" className="btn-primary mt-6 inline-flex">Browse &amp; save a search</Link>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {savedSearches.map(s => (
                <li key={s.id} className="py-4 flex items-center gap-4" data-testid={`saved-search-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={searchUrl(s.params)} onClick={() => s.new_count > 0 && seenSearch(s.id)}
                      className="font-display text-base truncate block hover:underline" data-testid={`saved-search-run-${s.id}`}>
                      {s.label}
                    </Link>
                    <div className="text-xs text-neutral-500 mt-1">
                      {s.match_count} item{s.match_count === 1 ? "" : "s"} match
                      {s.new_count > 0 && (
                        <span className="ml-2 font-medium" style={{color:"#2563EB"}} data-testid={`saved-search-new-${s.id}`}>
                          · {s.new_count} new
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => toggleEmail(s.id, s.email_alerts)} className="chip"
                    data-active={s.email_alerts} data-testid={`saved-search-email-${s.id}`}
                    title={s.email_alerts ? "Email alerts on — click to turn off" : "Email alerts off — click to turn on"}>
                    <Mail size={12} /> {s.email_alerts ? "Email on" : "Email off"}
                  </button>
                  <Link to={searchUrl(s.params)} onClick={() => s.new_count > 0 && seenSearch(s.id)}
                    className="btn-outline text-[0.62rem]" data-testid={`saved-search-view-${s.id}`}>View</Link>
                  <button onClick={()=>deleteSearch(s.id)} className="flex flex-col items-center p-2 hover:bg-black/5" title="Delete saved search" data-testid={`saved-search-del-${s.id}`}>
                    <Trash2 size={16}/>
                    <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Delete</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {moveFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setMoveFor(null)}>
          <div className="bg-white hairline p-8 w-full max-w-md" onClick={e=>e.stopPropagation()}>
            <div className="label-eyebrow">Move to category</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {CATEGORIES.map(c => (
                <button key={c} onClick={()=>move(moveFor, c)} className="hairline p-3 text-sm text-left hover:bg-black/5" data-testid={`cat-btn-${c}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
